import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { socket } from "../socket";
import api from "../utils/axiosInstance";

const NotificationContext = createContext();
export const useNotifications = () => useContext(NotificationContext);

export function NotificationProvider({ children }) {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  // Fetch unread count
  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await api.get("/api/notifications/unread-count");
      setUnreadCount(res.data.count || 0);
    } catch {}
  }, []);

  // Fetch full notification list
  const fetchNotifications = useCallback(async () => {
    try {
      const res = await api.get("/api/notifications");
      setNotifications(res.data.notifications || []);
    } catch {}
  }, []);

  // Mark single as read
  const markAsRead = useCallback(async (id) => {
    try {
      await api.put(`/api/notifications/read/${id}`);
      setNotifications((prev) => prev.map((n) => n.id === id ? { ...n, read: true } : n));
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch {}
  }, []);

  // Mark all as read
  const markAllRead = useCallback(async () => {
    try {
      await api.put("/api/notifications/read-all");
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch {}
  }, []);

  // Delete single
  const deleteNotif = useCallback(async (id, wasUnread) => {
    try {
      await api.delete(`/api/notifications/${id}`);
      setNotifications((prev) => prev.filter((n) => n.id !== id));
      if (wasUnread) setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch {}
  }, []);

  // Clear all
  const clearAll = useCallback(async () => {
    try {
      await api.delete("/api/notifications");
      setNotifications([]);
      setUnreadCount(0);
    } catch {}
  }, []);

  // Request browser notification permission on mount
  useEffect(() => {
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission();
    }
  }, []);

  // Listen for real-time notifications via Socket.IO
  useEffect(() => {
    const handler = (notif) => {
      setNotifications((prev) => [notif, ...prev].slice(0, 50));
      setUnreadCount((prev) => prev + 1);

      // Browser push notification if tab not focused
      if (document.hidden && "Notification" in window && Notification.permission === "granted") {
        try {
          new Notification(notif.title, {
            body: notif.message || "",
            icon: "/walytic-favicon.png",
            tag: notif.id,
          });
        } catch {}
      }

      // Play notification sound
      try {
        const audio = new Audio("/notification.mp3");
        audio.volume = 0.3;
        audio.play().catch(() => {});
      } catch {}
    };

    socket.on("notification:new", handler);
    return () => socket.off("notification:new", handler);
  }, []);

  // Fallback polling every 30s
  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  return (
    <NotificationContext.Provider value={{
      notifications, unreadCount, setNotifications, setUnreadCount,
      fetchNotifications, fetchUnreadCount, markAsRead, markAllRead,
      deleteNotif, clearAll,
    }}>
      {children}
    </NotificationContext.Provider>
  );
}
